/**
 * Hook for fetching device models from Supabase with pagination & server-side search
 *
 * - Initial load: first `PAGE_SIZE` items (sorted by is_most_chosen)
 * - loadMore(): appends next page
 * - searchTerm (from parent): resets list and fetches all matching results
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { getPublicDeviceModelsByType, PublicDeviceModel } from '../lib/publicDataAccess';

const PAGE_SIZE = 40;

export interface UseDeviceModelsResult {
  models: PublicDeviceModel[];
  isLoading: boolean;
  isLoadingMore: boolean;
  hasMore: boolean;
  totalCount: number | null;
  error: string | null;
  loadMore: () => void;
}

/**
 * Fetch device models with infinite-scroll pagination and server-side search.
 *
 * @param deviceType - Device type key (e.g., 'iPhone', 'Samsung')
 * @param language - Language for display names (default: 'nl')
 * @param searchTerm - Server-side search term (debounced by caller)
 */
export function useDeviceModels(
  deviceType: string,
  language: string = 'nl',
  searchTerm: string = ''
): UseDeviceModelsResult {
  const [models, setModels] = useState<PublicDeviceModel[]>([]);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [totalCount, setTotalCount] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Guard against stale responses when deps change rapidly
  const requestIdRef = useRef(0);

  const fetchPage = useCallback(async (pageNum: number, reset: boolean) => {
    if (!deviceType) {
      setModels([]);
      setIsLoading(false);
      setHasMore(false);
      return;
    }

    const reqId = ++requestIdRef.current;
    if (reset) { setIsLoading(true); setError(null); }
    else { setIsLoadingMore(true); }

    try {
      const result = await getPublicDeviceModelsByType(deviceType, language, {
        page: pageNum,
        limit: PAGE_SIZE,
        searchTerm: searchTerm || undefined,
      });

      // Discard if a newer request was issued while we were waiting
      if (reqId !== requestIdRef.current) return;

      setModels(prev => reset ? result.models : [...prev, ...result.models]);
      setHasMore(result.hasMore);
      if (result.totalCount !== undefined) setTotalCount(result.totalCount);
    } catch (err) {
      if (reqId !== requestIdRef.current) return;
      setError(err instanceof Error ? err.message : 'Failed to load device models');
      if (reset) setModels([]);
      setHasMore(false);
    } finally {
      if (reqId === requestIdRef.current) {
        setIsLoading(false);
        setIsLoadingMore(false);
      }
    }
  }, [deviceType, language, searchTerm]);

  // Reset & fetch first page whenever key inputs change
  useEffect(() => {
    setPage(0);
    setHasMore(true);
    fetchPage(0, true);
  }, [fetchPage]);

  const loadMore = useCallback(() => {
    if (!hasMore || isLoadingMore || isLoading) return;
    const nextPage = page + 1;
    setPage(nextPage);
    fetchPage(nextPage, false);
  }, [hasMore, isLoadingMore, isLoading, page, fetchPage]);

  return { models, isLoading, isLoadingMore, hasMore, totalCount, error, loadMore };
}

export default useDeviceModels;
